import { useEffect, useRef, useState } from 'react';

/**
 * Whether the category rail should retreat: true once the page has been scrolled down past
 * `threshold`, false again once it is scrolled back up.
 *
 * Used because the rail cannot scroll away on its own - the topbar is sticky and the rail sits
 * inside the same <header> the page layout gives it (see TopbarCategoryRail's .hidden rule).
 * It is also the row a reader scrolling into results needs least, and on a phone the bar plus
 * the rail is a large share of the viewport.
 *
 * Two thresholds, because they stop different things:
 *
 * - `threshold` is how far down the page the rail is allowed to hide at all. Near the top it
 *   always shows, which also covers iOS elastic overscroll putting scrollY below zero.
 * - `directionDelta` is how far a reversal has to be sustained before it counts. Without it any
 *   single pixel in the other direction flipped the rail, and trackpad and momentum scrolling
 *   change direction constantly - so the rail flapped open and shut.
 *
 * The rail lives in the sticky header's flow, so collapsing it takes its own height out of the
 * document above the reader and the browser's scroll anchoring compensates by moving scrollY by
 * exactly that much (measured on the live site: -46px on collapse, +46px on reveal). scrollY is
 * the only input this hook has, so the rail's own retreat arrived back here as a
 * deliberate-looking scroll up - and a larger one than `directionDelta` guards against, which is
 * why the hysteresis could not absorb what the rail itself had caused. The rail brought itself
 * back, the reveal pushed scrollY the other way, and it went again. A hide in the 46px band
 * directly above `threshold` also lands back inside it, where the near-the-top rule below shows
 * the rail unconditionally.
 *
 * So the reader's travel is measured against the top of the rail rather than the top of the
 * document: whatever the collapse removes is added back, leaving a number the rail's own state
 * cannot move. The compensation follows the rail's *measured* height rather than assuming the
 * full amount, because the collapse is a 0.1s transition - adding the whole height the moment
 * `isHidden` flips would itself read as a scroll of the rail's entire height.
 *
 * Reads are batched into an animation frame so a burst of scroll events costs one measurement
 * and at most one state change, rather than one of each per event.
 *
 * @param {Object} railRef ref to the rail element, so its own collapse can be measured out
 * @param {number} threshold pixels down the page before the rail may hide
 * @param {number} directionDelta pixels of sustained travel before a reversal counts
 * @returns {boolean} whether the rail should be hidden
 */
const useHideOnScrollDown = (railRef, threshold = 80, directionDelta = 16) => {
  const [isHidden, setIsHidden] = useState(false);
  const lastY = useRef(0);
  // Where the current run of travel in one direction began, and which way it goes.
  const anchorY = useRef(0);
  const direction = useRef(0);
  // The rail's height when open, learned by watching it rather than duplicating the CSS token.
  const openHeight = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let frame = null;

    const measure = () => {
      frame = null;

      // How much of the rail is collapsed right now: 0 while it is open, growing to its full
      // height as it retreats - in step with the scroll anchoring correction that collapse
      // causes, so the two cancel.
      const railHeight = railRef?.current ? railRef.current.getBoundingClientRect().height : 0;
      openHeight.current = Math.max(openHeight.current, railHeight);
      const collapsed = openHeight.current - railHeight;

      // Elastic overscroll reports a negative scrollY on iOS, which would read as "scrolled up
      // from zero" and is not a direction the reader chose.
      const y = Math.max(window.scrollY, 0) + collapsed;
      const delta = y - lastY.current;
      lastY.current = y;

      // Back within the threshold: show, whatever the direction hysteresis would otherwise say.
      // Returning to the top of a page should never leave the rail retreated, and a jump
      // straight there is one reversal rather than the sustained run the hysteresis wants.
      if (y <= threshold) {
        direction.current = delta > 0 ? 1 : -1;
        anchorY.current = y;
        setIsHidden(false);
        return;
      }

      if (delta === 0) {
        return;
      }

      const nextDirection = delta > 0 ? 1 : -1;
      if (nextDirection !== direction.current) {
        // A reversal starts a new run rather than acting immediately.
        direction.current = nextDirection;
        anchorY.current = y;
        return;
      }

      if (Math.abs(y - anchorY.current) < directionDelta) {
        return;
      }

      setIsHidden(nextDirection === 1 && y > threshold);
    };

    const onScroll = () => {
      if (frame === null) {
        frame = window.requestAnimationFrame(measure);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [railRef, threshold, directionDelta]);

  return isHidden;
};

export default useHideOnScrollDown;
